/**
 * Secure storage utilities for sensitive booking data.
 * Uses Web Crypto API for client-side encryption of PII before storing in sessionStorage.
 */

const STORAGE_KEY_PREFIX = 'airfly_secure_';
const ENCRYPTION_ALGO = 'AES-GCM';
const KEY_LENGTH = 256;

/**
 * Generate a cryptographic key from a password/seed.
 * Uses session-specific seed so key changes each browser session.
 */
async function deriveKey(password) {
  const encoder = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('airfly-booking-salt-v1'), // Static salt (ok for client-side protection)
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: ENCRYPTION_ALGO, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Get or create a session-specific encryption key.
 * Key exists only for current browser session, cleared on tab close.
 */
async function getSessionKey() {
  let sessionSeed = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}seed`);
  
  if (!sessionSeed) {
    // Generate random seed for this session
    const randomBytes = new Uint8Array(32);
    window.crypto.getRandomValues(randomBytes);
    sessionSeed = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem(`${STORAGE_KEY_PREFIX}seed`, sessionSeed);
  }

  return deriveKey(sessionSeed);
}

/**
 * Encrypt sensitive data before storing.
 */
async function encryptData(data) {
  try {
    const key = await getSessionKey();
    const encoder = new TextEncoder();
    const iv = window.crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for AES-GCM
    
    const encrypted = await window.crypto.subtle.encrypt(
      { name: ENCRYPTION_ALGO, iv },
      key,
      encoder.encode(JSON.stringify(data))
    );

    // Combine IV + encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    // Convert to base64 for storage
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    // Encryption failed
    return null;
  }
}

/**
 * Decrypt data retrieved from storage.
 */
async function decryptData(encryptedBase64) {
  try {
    const key = await getSessionKey();
    
    // Decode from base64
    const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
    
    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    const decrypted = await window.crypto.subtle.decrypt(
      { name: ENCRYPTION_ALGO, iv },
      key,
      encrypted
    );

    const decoder = new TextDecoder();
    return JSON.parse(decoder.decode(decrypted));
  } catch (error) {
    // Decryption failed
    return null;
  }
}

/**
 * Securely store sensitive booking data with encryption and expiry.
 * 
 * @param {string} key - Storage key
 * @param {object} data - Data to store
 * @param {number} ttlMinutes - Time to live in minutes (default: 30)
 */
export async function secureSetItem(key, data, ttlMinutes = 30) {
  try {
    const payload = {
      data,
      expiresAt: Date.now() + ttlMinutes * 60 * 1000,
    };

    const encrypted = await encryptData(payload);
    if (encrypted) {
      sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, encrypted);
      return true;
    }
    return false;
  } catch (error) {
    // Secure storage failed
    return false;
  }
}

/**
 * Retrieve and decrypt sensitive booking data.
 * Returns null if expired or decryption fails.
 * 
 * @param {string} key - Storage key
 */
export async function secureGetItem(key) {
  try {
    const encrypted = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`);
    if (!encrypted) {
      return null;
    }

    const payload = await decryptData(encrypted);
    if (!payload) {
      return null;
    }

    // Validate expiry
    if (payload.expiresAt && Date.now() > payload.expiresAt) {
      sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
      return null;
    }

    return payload.data;
  } catch (error) {
    // Secure retrieval failed
    return null;
  }
}

/**
 * Remove encrypted item from storage.
 */
export function secureRemoveItem(key) {
  try {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
    return true;
  } catch {
    return false;
  }
}

/**
 * Clear all secure storage items (useful on logout).
 */
export function secureClearAll() {
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
    return true;
  } catch {
    return false;
  }
}
